"use client";

import { HomeIcon, PlayIcon } from "@/components/common/icons";
import {
  IconProps,
  MerchIcon,
} from "@punchup/ui-common/components/common/icons";
import { LineChart, MapPinned, UserCog, Wrench } from "lucide-react";
import { MerchEvents, NavbarEvents, UserEvents, trackEvent } from "@/analytics";
import { NuxSlug, markNuxViewed, shouldDisplayNux } from "@/data/nux";
import { useCallback, useEffect } from "react";
import { useIsAuthorizedToSeeDashboards, useSession } from "@/hooks";

import { Database } from "@punchup/data/supabase.types";
import Link from "next/link";
import { cn } from "@/components/common/utils";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { isSuperAdmin } from "@punchup/data/legacy";
import { useGate } from "statsig-react";
import { usePathname } from "next/navigation";
import { useState } from "react";

interface NavItem {
  id: string;
  label: string;
  url: string;
  eventName?: string;
  Icon: React.FC<IconProps>;
  disabled: boolean;
  active: boolean;
}

export default function NewNavbar({
  className,
  listClassName,
  itemClassName,
  iconClassName,
}: {
  className?: string;
  listClassName?: string;
  itemClassName?: string;
  iconClassName?: string;
}) {
  const session = useSession();
  const [userIsSuperAdmin, setUserIsSuperAdmin] = useState(false);
  const [showDashboardNux, setShowDashboardNux] = useState(false);
  const supabase = createClientComponentClient<Database>();
  const { userIsAuthorizedToSeeAnyDashboards } =
    useIsAuthorizedToSeeDashboards();
  const pathname = usePathname();
  const { value: subscriptionsNavEnabled } = useGate("subscriptions_nav_bar");

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const userAdminStatus = await isSuperAdmin(supabase);
        setUserIsSuperAdmin(userAdminStatus);
      } catch (error) {
        // Handle any errors that may occur during the promise resolution.
        console.error("Error in isComedian:", error);
        throw error; // Rethrow the error or handle it further up the call stack.
      }
    };

    checkUserRole();
  }, [supabase, session]);

  const fetchNuxViews = useCallback(async () => {
    if (!session?.user) return;

    if (userIsAuthorizedToSeeAnyDashboards) {
      shouldDisplayNux(session.user, NuxSlug.NavLinkDashboard).then(
        (result) => {
          setShowDashboardNux(result);
        }
      );
    }
  }, [session, userIsAuthorizedToSeeAnyDashboards]);

  useEffect(() => {
    fetchNuxViews();
  }, [fetchNuxViews]);

  useEffect(() => {
    // Set data attribute on root element for CSS variable control
    document.documentElement.setAttribute(
      "data-subscriptions-nav",
      String(subscriptionsNavEnabled)
    );
  }, [subscriptionsNavEnabled]);

  const navItems: NavItem[] = [
    {
      id: "home",
      label: "Home",
      url: "/",
      eventName: NavbarEvents.NAVIGATE_TO_HOME,
      Icon: ({ className }: IconProps) => <HomeIcon className={className} />,
      disabled: false,
      active: pathname === "/",
    },
    {
      id: "library",
      label: subscriptionsNavEnabled ? "Subscriptions" : "Library",
      url: session?.user ? "/library" : "/signin",
      eventName: NavbarEvents.NAVIGATE_TO_LIBRARY,
      Icon: ({ className }: IconProps) => (
        <div>
          <PlayIcon className={className} />
        </div>
      ),
      disabled: false,
      active: pathname === "/library",
    },
    {
      id: "shows-nearby",
      label: "Shows",
      url: "/nearby",
      eventName: NavbarEvents.NAVIGATE_TO_NEARBY_SHOWS,
      Icon: ({ className }: IconProps) => (
        <div className="relative">
          <MapPinned className={`${className}`} />
        </div>
      ),
      disabled: false,
      active: pathname === "/nearby",
    },
    {
      id: "merch",
      label: "Merch",
      url: "/merch",
      eventName: MerchEvents.NAVIGATE_TO_MERCH,
      Icon: ({ className }: IconProps) => (
        <div className="relative">
          <MerchIcon className={`stroke-0 ${className}`} />
        </div>
      ),

      disabled: false,
      active: pathname === "/merch",
    },
  ];

  if (userIsSuperAdmin) {
    navItems.splice(1, 0, {
      id: "internal",
      label: "Internal",
      url: "/internal",
      Icon: ({ className }: IconProps) => (
        <div className="relative">
          <Wrench className={className} />
        </div>
      ),
      disabled: false,
      active: pathname.startsWith("/internal"),
    });
  }

  if (userIsAuthorizedToSeeAnyDashboards) {
    const pathSegments = pathname
      .split("/")
      .filter((segment) => segment !== "");

    const firstPathSegment = pathSegments[0];
    const secondPathSegment = pathSegments[1];
    const thirdPathSegment = pathSegments[2];

    // on the "/dashboard" page
    const onDashboardPage = pathname.startsWith("/dashboard");

    // i.e. "akaash/dashboard" OR "akaash/dashboard/some/page"
    const onComedianAdminPage =
      pathSegments.length >= 2 && secondPathSegment === "dashboard";

    // i.e. "v/stmarkscomedyclub/dashboard" OR "v/stmarkscomedyclub/dashboard/some/page"
    const onVenueAdminPage =
      pathSegments.length >= 3 &&
      firstPathSegment === "v" &&
      thirdPathSegment === "dashboard";

    // if we're on the dashboard page, or on a comedian or venue admin page, the Admin button should be active
    const active = onDashboardPage || onComedianAdminPage || onVenueAdminPage;

    navItems.splice(userIsSuperAdmin ? 2 : 1, 0, {
      id: "admin",
      label: "Admin",
      url: "/dashboard",
      eventName: UserEvents.NAVIGATE_TO_DASHBOARD_FROM_NAV_BUTTON,
      Icon: ({ className }: IconProps) => (
        <div className="relative">
          <UserCog width={24} height={24} className={className} />
        </div>
      ),
      disabled: false,
      active,
    });
  }

  const enabledNavItems = navItems.filter((item) => !item.disabled);

  if (enabledNavItems.length === 0) return null;

  return (
    <div className="flex flex-col h-full">
      <nav
        className={cn(
          "w-navbar md:h-full fixed left-0 right-0 bottom-0 pb-safe px-safe z-40 overflow-visible flex flex-col justify-center gap-2 bg-ivory border-t md:border-t-0 md:border-r border-[var(--punchup-color-zinc-200)] pt-0 md:pt-[var(--header-height)]",
          className
        )}
      >
        <ul
          className={cn(
            "h-navbar md:h-auto flex md:flex-col flex-row items-center justify-around w-full",
            listClassName
          )}
          style={{
            gridTemplateColumns: `repeat(${enabledNavItems.length}, minmax(0, 1fr))`,
          }}
        >
          {enabledNavItems.map(
            ({ id, label, Icon, url, eventName, disabled, active }) => (
              <li
                key={id}
                className="flex flex-row justify-center group w-full md:py-1.5 md:w-[unset] relative"
              >
                {/* show a "nux" for users that haven't seen the new Admin menu item */}
                {showDashboardNux && id === "admin" && (
                  <NewAdminMenuItemNux
                    onClick={() => {
                      // hide the nux
                      setShowDashboardNux(false);
                    }}
                  />
                )}
                <Link
                  href={url}
                  onClick={() => {
                    if (eventName) {
                      trackEvent(eventName, {
                        location: "navbar",
                        disabled,
                      });
                    }
                    // if the nux is shown and the user clicks the menu item
                    if (showDashboardNux && id === "admin") {
                      // mark the nux as viewed
                      markNuxViewed(supabase, NuxSlug.NavLinkDashboard);
                      // hide the nux
                      setShowDashboardNux(false);
                    }
                  }}
                  className={cn(
                    "group w-full p-0.5 flex flex-col items-center justify-center md:gap-[1px] rounded-md",
                    disabled ? "opacity-50 cursor-not-allowed" : "",
                    active
                      ? "text-primary stroke-primary fill-primary rounded-md font-semibold stroke-[3] md:group-hover:text-[#ff5317]"
                      : "text-slate-gray-900 stroke-slate-gray-900 fill-slate-gray-900",
                    itemClassName
                  )}
                  aria-label={label}
                >
                  <Icon
                    className={cn(
                      "h-6 w-6 md:w-8 md:h-8 rounded-md text-inherit md:p-[5px] transition-colors ease-in-out duration-200",
                      active
                        ? "md:bg-[#FFE3D8] md:group-hover:bg-[#ffd2c0]"
                        : "md:group-hover:bg-[#e5d6c3]/50",
                      iconClassName
                    )}
                  />
                  <span className="text-[12px] whitespace-nowrap">{label}</span>
                </Link>
              </li>
            )
          )}
        </ul>
      </nav>
    </div>
  );
}

const NewAdminMenuItemNux = ({ onClick }: { onClick: () => void }) => {
  const supabase = createClientComponentClient<Database>();

  const onClickHandler = () => {
    markNuxViewed(supabase, NuxSlug.NavLinkDashboard);
    onClick();
  };

  return (
    <div
      className={cn(
        "absolute z-50 p-3 text-sm rounded-lg shadow-xl w-max max-w-[250px]",
        "bg-slate-800 text-slate-100 shadow-lg",
        "bottom-full left-1/2 -translate-x-1/2 mb-2.5",
        "md:left-full md:top-1/2 md:bottom-auto md:translate-x-0 md:-translate-y-1/2 md:ml-2.5 md:mb-0"
      )}
      onClick={onClickHandler}
    >
      <div className=" flex flex-row">
        <div className="flex flex-col items-center justify-center gap-1.5">
          <p className="font-medium text-center">
            Manage your page, view your data, audience and more here
          </p>
          <div className="flex flex-row items-center justify-center gap-2">
            <p className="font-medium text-center">previously</p>
            <p className="flex flex-row text-[16px] items-center gap-2 font-medium text-center">
              <LineChart /> Dashboard
            </p>
          </div>
        </div>
      </div>
      <div
        className={cn(
          "absolute left-1/2 -translate-x-1/2 w-0 h-0",
          "border-l-[7px] border-l-transparent",
          "border-r-[7px] border-r-transparent",
          "border-t-[7px] border-t-slate-800",
          "bottom-[-7px]",
          "md:hidden"
        )}
      />
      <div
        className={cn(
          "absolute top-1/2 -translate-y-1/2 w-0 h-0",
          "border-t-[7px] border-t-transparent",
          "border-b-[7px] border-b-transparent",
          "border-r-[7px] border-r-slate-800",
          "left-[-7px]",
          "hidden md:block"
        )}
      />
    </div>
  );
};
